/* eslint-disable no-debugger */
import { useState, useEffect } from 'react';
import useForm from '@usas/ui-web/utils/UseForm';
import validate from "./ClubMembershipDetailReportValidate";
import useReportPeriodFlatData from '@usas/ui-web/state/reportPeriodFlat/UseReportPeriodFlatData';
import Constants from '@usas/ui-web/utils/Constants';
import useOrgUnitData from '../../../state/orgUnit/UseOrgUnitData';

// Define the columns configuration for this report
const columns = [
  { width: "110px", id: "SeasonYear", field: "seasonYear", title: "Season Year" },
  { width: "130px", id: "ZoneName", field: "zoneName", title: "Zone Name" },
  { width: "110px", id: "LscCode", field: "lscCode", title: "LSC Code" },
  { width: "220px", id: "ClubName", field: "clubName", title: "Club Name" },
  { width: "300px", id: "FullAddress", field: "fulladdress", title: "Address" },
  { width: "180px", id: "OfferingName", field: "offeringName", title: "Offering Name" },
  { width: "180px", id: "ClubOwnerType", field: "clubownertype", title: "Club Owner Type" },
  { width: "150px", id: "ClubSetting", field: "clubsetting", title: "Club Setting" },
  { width: "200px", id: "BusinessEntityType", field: "businessentitytype", title: "Business Entity Type" },
  { width: "120px", id: "RegistrationDate", field: "registrationdate", title: "Registration Date", format: "{0:MM/dd/yyyy}" },
  { width: "120px", id: "ExpirationDate", field: "expirationdate", title: "Expiration Date", format: "{0:MM/dd/yyyy}" },
  { width: "120px", id: "Clbms101CompletionDate", field: "clbms101completiondate", title: "CLBMS 101 Completion Date", format: "{0:MM/dd/yyyy}" },
  { width: "120px", id: "Clbms201CompletionDate", field: "clbms201completiondate", title: "CLBMS 201 Completion Date", format: "{0:MM/dd/yyyy}" },
  { width: "190px", id: "HeadCoachName", field: "headcoachname", title: "Head Coach Name" },
  { width: "240px", id: "HeadCoachEmail", field: "headcoachemail", title: "Head Coach Email" },
  { width: "190px", id: "ClubContactName", field: "clubcontactname", title: "Club Contact Name" },
  { width: "240px", id: "ClubContactEmail", field: "clubcontactemail", title: "Club Contact Email" },
  { width: "95px", id: "YearFounded", field: "yearfounded", title: "Year Founded" },
  { width: "270px", id: "NotInGoodStandingReason", field: "notingoodstandingreason", title: "Not In Good Standing Reason" },
  { width: "120px", id: "Category", field: "category", title: "Category" },
  { width: "110px", id: "TotalMembers", field: "totalMembers", title: "Total Members" },
  { width: "240px", id: "WebsiteAddress", field: "websiteaddress", title: "Website Address" }
];

// Facility columns only fill when exportDetailData is on
const excelColumns = [
  { id: "SeasonYear", field: "seasonYear", title: "Season Year" },
  { id: "ZoneName", field: "zoneName", title: "Zone Name" },
  { id: "LscCode", field: "lscCode", title: "LSC Code" },
  { id: "ClubName", field: "clubName", title: "Club Name" },
  { id: "FullAddress", field: "fulladdress", title: "Address" },
  { id: "OfferingName", field: "offeringName", title: "Offering Name" },
  { id: "ClubOwnerType", field: "clubownertype", title: "Club Owner Type" },
  { id: "ClubSetting", field: "clubsetting", title: "Club Setting" },
  { id: "BusinessEntityType", field: "businessentitytype", title: "Business Entity Type" },
  { id: "RegistrationDate", field: "registrationdate", title: "Registration Date" },
  { id: "ExpirationDate", field: "expirationdate", title: "Expiration Date" },
  { id: "Clbms101CompletionDate", field: "clbms101completiondate", title: "CLBMS 101 Completion Date" },
  { id: "Clbms201CompletionDate", field: "clbms201completiondate", title: "CLBMS 201 Completion Date" },
  { id: "HeadCoachName", field: "headcoachname", title: "Head Coach Name" },
  { id: "HeadCoachEmail", field: "headcoachemail", title: "Head Coach Email" },
  { id: "ClubContactName", field: "clubcontactname", title: "Club Contact Name" },
  { id: "ClubContactEmail", field: "clubcontactemail", title: "Club Contact Email" },
  { id: "YearFounded", field: "yearfounded", title: "Year Founded" },
  { id: "NotInGoodStandingReason", field: "notingoodstandingreason", title: "Not In Good Standing Reason" },
  { id: "Category", field: "category", title: "Category" },
  { id: "TotalMembers", field: "totalMembers", title: "Total Members" },
  { id: "WebsiteAddress", field: "websiteaddress", title: "Website Address" },
  { id: "FacilityDetail_FacilityName", field: "facilityDetail_facilityName", title: "Facility Name" },
  { id: "FacilityDetail_FacilityAddress", field: "facilityDetail_facilityAddress", title: "Facility Address" },
  { id: "FacilityDetail_FacilityContact", field: "facilityDetail_facilityContact", title: "Facility Contact" },
  { id: "FacilityDetail_FacilityContactEmail", field: "facilityDetail_facilityContactEmail", title: "Facility Contact Email" },
  { id: "FacilityDetail_FacilityContactPhone", field: "facilityDetail_facilityContactPhone", title: "Facility Contact Phone" }
];

const useClubMembershipDetailReport = () => {
  const { reportPeriodFlatState, getReportPeriodsFlatByOrganizationId } = useReportPeriodFlatData();
  const [seasonYearState, setSeasonYearState] = useState({ isLoading: true, isLoaded: false, options: [] });
  const [modalState, setModalState] = useState(getInitialModalState());
  const { orgUnitState } = useOrgUnitData();

  const {
    formState,
    errorState,
    handleSubmit,
    updateFormState,
    onFormValueChanged,
    onValueTextPairChanged,
    setFormState,
    setIsDirty
  } = useForm(getInitialFormState(), submitFormCallback, validate);

  function submitFormCallback() {
    setModalState({ ...modalState, displayPopUp: true });
  }

  const onRunReportClicked = (e) => {
    handleSubmit(e);
  }

  const onClearForm = () => {
    setFormState(getInitialFormState());
    setIsDirty(true);
  }

  const onModalCancelClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setIsDirty(true);
    setModalState({ ...modalState, displayPopUp: false });
  };

  function getInitialModalState() {
    return {
      displayPopUp: false,
      currentPrimaryInfo: '',
      modalTitle: 'Report Viewer'
    };
  }

  function getInitialFormState() {
    return {
      orgUnitId: '',
      seasonId: -1,
      seasonYear: -1,
      clubs: [],
      lscs: [],
      zones: [],
      genders: [],
      displayOptions: true,
      clubOwnerTypes: [],
      notInGoodStanding: false,
      exportDetailData: false
    };
  }

  useEffect(() => {
    if (reportPeriodFlatState.isLoading === false && reportPeriodFlatState.isLoaded === false) {
      getReportPeriodsFlatByOrganizationId(1);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (reportPeriodFlatState.isLoaded === true) {
      const years = [];

      reportPeriodFlatState.dataAsOptionsWithNames.map(rp => {
        if (rp.name.substring(5, 6) === '(') {
          years.unshift({ id: rp.id, name: rp.name.substring(0, 4) });
        }
      });
      years.unshift({ id: -1, name: '--' });
      setSeasonYearState({ isLoading: false, isLoaded: true, options: years });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportPeriodFlatState]);

  function getClubFileterIds() {
    if (orgUnitState.arrayData.find(ou => ou.orgUnitId === formState.orgUnitId)?.orgLevelId === 2) { 
      return formState.orgUnitId.toString();
    } else  {
      return formState.lscs.map(c => c.id).join(',')
    } 
  }

  return {
    formState,
    errorState,
    setFormState,
    reportParameters: {
      OrgUnitId_Int: formState.orgUnitId === "" ? null : formState.orgUnitId,
      Zones: formState.zones.map(zone => zone.id).join(',').length === 0 ? null : formState.zones.map(zone => zone.id).join(','), 
      LSCs: formState.lscs.map(lsc => lsc.id).join(',') || null, 
      Clubs: formState.clubs.map(club => club.id).join(',') || null, 
      SeasonYear_Int: formState.seasonYear === Constants.DEFAULT_ID ? null : parseInt(formState.seasonYear, 10),
      ClubTypes: formState.clubOwnerTypes.map(cot => cot.name).join(',') || null, 
      NotGoodStanding_Int: formState.notInGoodStanding === true ? 1 : 0
    },
    parameters: formState,
    columns: columns,
    modalState,
    orgUnitState,
    onModalCancelClicked,
    handleSubmit,
    onFormValueChanged,
    onValueTextPairChanged,
    updateFormState,
    onRunReportClicked,
    onClearForm,
    seasonYearState,
    excelColumns: excelColumns,
    clubFilterIds: getClubFileterIds()
  };
};

export default useClubMembershipDetailReport;